<template>
    <div v-if="combinedEmptyState" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
        <h2 class="body-serif text-ink">{{ combinedEmptyState.title }}</h2>
        <p class="body-small mt-1 max-w-md text-balance text-ink-muted">{{ combinedEmptyState.body }}</p>
    </div>
    <div v-else class="flex flex-col gap-6">
        <template v-for="tab in profileTabIds" :key="tab">
            <section v-if="emptyStates[tab]" class="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-white">
                <h2 class="body-serif px-6 py-4 text-ink">{{ profileTabLabels[tab] }}</h2>
                <div class="flex flex-col items-center p-6 text-center">
                    <p class="body-serif text-ink">{{ emptyStates[tab].title }}</p>
                    <p class="body-small mt-1 text-ink-muted">{{ emptyStates[tab].body }}</p>
                </div>
            </section>
            <ProfileTopicSection
                v-else
                :is-loading="isProfileTopicsLoading"
                :error="profileTopicsErrorMessage"
                :has-profile="hasPublicProfile"
                :entries="tabEntries[tab]"
                :empty-profile-state="noProfileState"
                :empty-entries-state="noProfileState"
                :heading="profileTabIds.length > 1 ? profileTabLabels[tab] : undefined"
                compact />
        </template>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ProfileEntry } from "~vue/components/ProfileTopicSection.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        targetUserId: string | null;
        publicProfileEnabled: boolean;
    }>(),
    {
        targetUserId: null,
        publicProfileEnabled: false,
    },
);

enum ProfileTabId {
    WorkingWithMe = "working",
    TeamWork = "team",
}

const profileTabLabels = computed(() => ({
    working: t("components.profile.public.workingWithMe"),
    team: t("components.profile.public.howTeamShouldWork"),
}));
const profileTabIds = computed(() => {
    const ids = [ProfileTabId.WorkingWithMe];
    if (isManager.value) {
        ids.push(ProfileTabId.TeamWork);
    }
    return ids;
});

interface PropertySubtopic {
    id: string;
    title: string;
    placeholder: string;
    order: number;
    current?: {
        content: string;
        access: string;
    };
}

interface PropertyTopic {
    id: string;
    topic: string;
    placeholder: string;
    managers_only?: boolean;
    subtopics: PropertySubtopic[];
}

interface PropertyTopicsResponse {
    topics: PropertyTopic[];
}

const {
    data: profileTopics,
    isFetching: isProfileTopicsLoading,
    error: profileTopicsError,
    execute: fetchProfileTopics,
    statusCode: profileTopicsStatusCode,
} = useFetch(() => (props.targetUserId ? `/api/accounts/profile/topics/${props.targetUserId}/` : ""), { immediate: false, refetch: true }).json<PropertyTopicsResponse>();

const hasPublicProfile = computed(() => {
    if (isProfileTopicsLoading.value) {
        return true;
    }
    if (profileTopicsError.value && profileTopicsStatusCode.value == 404) {
        // Treat the "not found" error case as the target user not having a public profile.  This
        // can happen if the target user deleted their public profile, or if they moved to a
        // different org from the current user.
        return false;
    }
    return true;
});

const profileTopicsErrorMessage = computed(() => {
    if (!profileTopicsError.value) {
        return null;
    }
    if (profileTopicsStatusCode.value == 404) {
        // We handle the not-found case as hasPublicProfile = false
        return null;
    }
    if (typeof profileTopicsError.value === "string") {
        return profileTopicsError.value;
    }
    return profileTopicsError.value.message || t("components.profile.public.loadFailed");
});

const shouldFetchProfileTopics = computed(() => props.publicProfileEnabled && props.targetUserId !== null);

watch(
    shouldFetchProfileTopics,
    async (shouldFetch) => {
        if (shouldFetch) {
            await fetchProfileTopics();
        }
    },
    { immediate: true },
);

const profileTopicMap = computed(() => {
    const topics = profileTopics.value?.topics ?? [];
    return new Map(topics.map((topic) => [topic.topic, topic]));
});

function buildProfileEntries(topicName: string): ProfileEntry[] {
    const topic = profileTopicMap.value.get(topicName);
    if (!topic) {
        return [];
    }

    return topic.subtopics
        .filter((subtopic) => Boolean(subtopic.current?.content?.trim()))
        .map((subtopic) =>
            toProfileEntry({
                id: subtopic.id,
                title: subtopic.title,
                content: subtopic.current?.content ?? "",
                placeholder: subtopic.placeholder,
            }),
        );
}

const tabEntries = computed<Record<ProfileTabId, ProfileEntry[]>>(() => ({
    [ProfileTabId.WorkingWithMe]: buildProfileEntries("How to Work With Me"),
    [ProfileTabId.TeamWork]: buildProfileEntries("How My Team Should Work"),
}));

const noProfileState = computed(() => ({ title: t("components.profile.public.noProfileTitle"), body: t("components.profile.public.noProfileBody"), icon: "" }));

const emptyStates = computed(() => {
    const noResponses = {
        [ProfileTabId.WorkingWithMe]: t("components.profile.public.noWorkingResponsesBody"),
        [ProfileTabId.TeamWork]: t("components.profile.public.noTeamResponsesBody"),
    };
    return Object.fromEntries(
        Object.values(ProfileTabId).map((tab) => {
            if (isProfileTopicsLoading.value || profileTopicsErrorMessage.value) {
                return [tab, null];
            }
            if (!hasPublicProfile.value) {
                return [tab, { title: noProfileState.value.title, body: noProfileState.value.body }];
            }
            return [tab, tabEntries.value[tab].length ? null : { title: t("components.profile.public.noResponsesTitle"), body: noResponses[tab] }];
        }),
    ) as Record<ProfileTabId, { title: string; body: string } | null>;
});

// One empty state for the whole section rather than a stack of identical empty cards.
const combinedEmptyState = computed(() => {
    if (!profileTabIds.value.every((tab) => emptyStates.value[tab])) {
        return null;
    }
    if (profileTabIds.value.length === 1 || !hasPublicProfile.value) {
        return emptyStates.value[profileTabIds.value[0]];
    }
    return { title: t("components.profile.public.noResponsesTitle"), body: t("components.profile.public.noResponsesBody") };
});

const isManager = computed(() => {
    // Check if any topic has managers_only flag set to true
    const topics = profileTopics.value?.topics ?? [];
    return topics.some((topic) => topic.managers_only === true);
});
</script>
